/**
 * Contact Us Component
 * Modern implementation of the contact us page functionality
 */

export class ContactUs {
    constructor() {
        this.isVisible = false;
        this.container = null;
    }

    init() {
        this.createContactModal();
        this.attachEventListeners();
    }

    createContactModal() {
        this.container = document.createElement('div');
        this.container.className = 'contact-modal-overlay';
        this.container.style.display = 'none';

        this.container.innerHTML = `
            <div class="contact-modal">
                <div class="contact-header">
                    <h2>Contact Us for Information About Premium Access</h2>
                    <button class="modal-close" id="contactClose">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <div class="contact-content">
                    <div class="contact-form">
                        <form id="contactForm" class="contact-form-fields">
                            <div class="form-group">
                                <label for="contactName">Name</label>
                                <input type="text" id="contactName" name="name" required>
                            </div>

                            <div class="form-group">
                                <label for="contactCompany">Company</label>
                                <input type="text" id="contactCompany" name="company" required>
                            </div>

                            <div class="form-group">
                                <label for="contactEmail">Email</label>
                                <input type="email" id="contactEmail" name="email" required>
                            </div>

                            <div class="form-group">
                                <label for="contactSubject">Subject</label>
                                <select id="contactSubject" name="subject" required>
                                    <option value="">Select a topic</option>
                                    <option value="data-inquiry">Data Inquiry</option>
                                    <option value="technical-issue">Technical Issue</option>
                                    <option value="feature-request">Feature Request</option>
                                    <option value="partnership">Partnership</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label for="contactMessage">Additional Information</label>
                                <textarea id="contactMessage" name="message" rows="4" placeholder="Any additional information or questions..."></textarea>
                            </div>

                            <div class="form-actions">
                                <button type="submit" class="btn-primary">Send Message</button>
                                <button type="button" class="btn-secondary" id="contactCancel">Cancel</button>
                            </div>
                        </form>
                    </div>

                    <div class="contact-response-note">
                        We will respond to your inquiry shortly.
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(this.container);
    }

    attachEventListeners() {
        // Close modal handlers
        const closeBtn = this.container.querySelector('#contactClose');
        const cancelBtn = this.container.querySelector('#contactCancel');
        const overlay = this.container;

        closeBtn?.addEventListener('click', () => this.hide());
        cancelBtn?.addEventListener('click', () => this.hide());

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) this.hide();
        });

        // Form submission
        const form = this.container.querySelector('#contactForm');
        form?.addEventListener('submit', (e) => this.handleSubmit(e));

        // Escape key handler
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isVisible) {
                this.hide();
            }
        });
    }

    async handleSubmit(event) {
        event.preventDefault();

        const form = event.target;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData);

        const submitBtn = form.querySelector('.btn-primary');
        const originalText = submitBtn.textContent;

        try {
            // Show loading state
            submitBtn.textContent = 'Sending...';
            submitBtn.disabled = true;

            // Send email using EmailJS (if configured)
            if (window.emailjs && import.meta.env.VITE_EMAILJS_SERVICE_ID) {
                // Format message with sender's details
                const formattedMessage = `From: ${data.name} <${data.email}>
Company: ${data.company}
Subject: ${data.subject}
Reply To: ${data.email}

Message:
${data.message || 'No additional information provided.'}`;

                // Template parameters matching EmailJS template variables
                const templateParams = {
                    from_name: data.name,
                    from_email: data.email,
                    reply_to: data.email,
                    subject: `[${data.subject}] Premium Access Inquiry from ${data.name} (${data.company})`,
                    message: formattedMessage,
                    to_email: 'info@globalminingrisk.com'
                };

                // Modern EmailJS API with options object
                await window.emailjs.send(
                    import.meta.env.VITE_EMAILJS_SERVICE_ID,
                    import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
                    templateParams,
                    {
                        publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY
                    }
                );

                this.showSuccess('Message sent successfully!');
                form.reset();

                setTimeout(() => this.hide(), 2000);
            } else {
                // Fallback to mailto
                const subject = encodeURIComponent(`[${data.subject}] Premium Access Inquiry from ${data.name} (${data.company})`);
                const body = encodeURIComponent(
                    `Name: ${data.name}\nCompany: ${data.company}\nEmail: ${data.email}\nSubject: ${data.subject}\n\nMessage:\n${data.message || 'N/A'}`
                );
                window.location.href = `mailto:info@globalminingrisk.com?subject=${subject}&body=${body}`;

                this.showSuccess('Opening email client...');
                setTimeout(() => this.hide(), 1500);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            this.showError('Failed to send message. Please try again or email us directly.');
        } finally {
            // Reset button state
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
        }
    }

    showSuccess(message) {
        this.showNotification(message, 'success');
    }

    showError(message) {
        this.showNotification(message, 'error');
    }

    showNotification(message, type) {
        // Use existing notification system if available
        if (window.notificationSystem) {
            if (type === 'success') {
                window.notificationSystem.showSuccess(message);
            } else {
                window.notificationSystem.showError(message);
            }
        } else {
            // Fallback notification
            alert(message);
        }
    }

    show() {
        this.isVisible = true;
        this.container.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        // Focus first input
        setTimeout(() => {
            const firstInput = this.container.querySelector('input');
            firstInput?.focus();
        }, 100);
    }

    hide() {
        this.isVisible = false;
        this.container.style.display = 'none';
        document.body.style.overflow = '';
    }

    destroy() {
        if (this.container) {
            this.container.remove();
            this.container = null;
        }
        this.isVisible = false;
    }
}

// Export singleton instance
export const contactUs = new ContactUs();